'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalTitle,
} from '@/components/extension/responsive-modal'
import { PopupStandaloneControl } from '@/components/standalone/popup'
import React, {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
} from 'react'

// Type for popup context
interface PopupContextType {
  openPopup: (element: React.ReactNode, callbackAfterClose?: () => void) => void // Cho phép truyền callback khi đóng
  closePopup: () => void
}

// Create context with default values
const PopupContext = createContext<PopupContextType>({
  openPopup: () => {},
  closePopup: () => {},
})

// Hook to use popup context
export const usePopup = () => useContext(PopupContext)

// Type cho global API
// Bổ sung callbackAfterClose
type PopupAPI = {
  openPopup: (
    element: React.ReactNode,
    callbackAfterClose?: () => void
  ) => PopupStandaloneControl
  closePopup: () => void
}

// Create a global reference to store the API
const popupApiRef: { current: PopupAPI | null } = { current: null }

// Export global functions that don't require hooks
// Bổ sung callbackAfterClose cho showPopup
export const showPopup = (
  element: React.ReactNode,
  callbackAfterClose?: () => void
): PopupStandaloneControl => {
  if (popupApiRef.current) {
    return popupApiRef.current.openPopup(element, callbackAfterClose)
  } else {
    console.warn('Popup provider not mounted yet')
    return {
      close: () => {},
      update: () => {},
    }
  }
}

export const hidePopup = () => {
  if (popupApiRef.current) {
    popupApiRef.current.closePopup()
  }
}

export const PopupProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [popupContent, setPopupContent] = useState<React.ReactNode>(null)
  const popupIdRef = useRef(0)
  const currentPopupRef = useRef<number | null>(null)
  // Lưu callbackAfterClose cho popup hiện tại
  const callbackAfterCloseRef = useRef<(() => void) | null>(null)

  // Function to open popup với JSX element và callbackAfterClose
  const openPopup = (
    element: React.ReactNode,
    callbackAfterClose?: () => void
  ): PopupStandaloneControl => {
    const popupId = ++popupIdRef.current
    currentPopupRef.current = popupId
    callbackAfterCloseRef.current = callbackAfterClose || null

    setPopupContent(element)
    setIsOpen(true)

    // Trả về control object cho popup này
    const control: PopupStandaloneControl = {
      close: () => {
        // Chỉ đóng nếu đúng popup đang active
        if (currentPopupRef.current === popupId) {
          closePopup()
        }
      },
      update: (newContent: React.ReactNode) => {
        // Chỉ update nếu đúng popup đang active
        if (currentPopupRef.current === popupId) {
          setPopupContent(newContent)
        }
      },
    }

    return control
  }

  // Function to close popup
  const closePopup = () => {
    setIsOpen(false)
    // Reset content sau khi animation hoàn tất
    setTimeout(() => {
      // Gọi callbackAfterClose trước khi reset state
      if (callbackAfterCloseRef.current) {
        callbackAfterCloseRef.current()
        callbackAfterCloseRef.current = null
      }
      // Reset state sau khi đã gọi callback
      setPopupContent(null)
      currentPopupRef.current = null
    }, 300)
  }

  // Set up the global API reference
  React.useEffect(() => {
    popupApiRef.current = {
      openPopup,
      closePopup,
    }

    return () => {
      popupApiRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <PopupContext.Provider
      value={{
        openPopup: (element, callbackAfterClose) => {
          openPopup(element, callbackAfterClose)
        },
        closePopup,
      }}
    >
      {children}
      <ResponsiveModal
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            closePopup()
          }
        }}
      >
        <ResponsiveModalContent
          onInteractOutside={(e) => {
            e.preventDefault()
            closePopup()
          }}
          aria-describedby={'global-popup'}
        >
          <ResponsiveModalTitle className='hidden' />
          {popupContent}
        </ResponsiveModalContent>
      </ResponsiveModal>
    </PopupContext.Provider>
  )
}
